import { queryOptions, useQuery } from '@tanstack/react-query';
import { useApi } from '../../api/api-context';
import { useMemo } from 'react';
/**
 * Fetches the authenticated user details.
 *
 * @param {ReturnType<typeof useApi>} api - API instance for making requests.
 * @returns {Promise<User>} - A promise resolving to the user data.
 */
const fetchUser = async (api) => {
    const response = await api.get('/user');
    return response.data;
};
/**
 * Exposed query options for fetching the authenticated user.
 *
 * @param {ReturnType<typeof useApi>} api - API instance.
 * @returns Query options for React Query.
 */
export const getUserQueryOptions = (api) => queryOptions({
    queryKey: ['user'],
    queryFn: () => fetchUser(api),
});
/**
 * React Query hook for fetching the authenticated user.
 *
 * @param {UseUserOptions} options - Optional configuration for the query.
 * @returns React Query response object.
 */
export const useGetUserApi = ({ queryConfig } = {}) => {
    const api = useApi();
    return useQuery({
        ...getUserQueryOptions(api),
        ...queryConfig,
    });
};
/**
 * Hook to check if a user is currently logged in.
 *
 * @returns {boolean} - `true` if a user is logged in, otherwise `false`.
 */
export const useIsUserLoggedIn = () => {
    const { data } = useGetUserApi();
    return !!data;
};
/**
 * Returns the user's timeZone, falling back to UTC if not supported.
 *
 * @returns {string} - `timeZone`.
 */
export function useTimeZone() {
    const { data } = useGetUserApi();
    return useMemo(() => {
        let candidate = data?.timeZone ?? 'UTC';
        try {
            Intl.DateTimeFormat(undefined, { timeZone: candidate }).format();
        }
        catch {
            console.warn(`Invalid timeZone “${data?.timeZone}”, defaulting to UTC`);
            candidate = 'UTC';
        }
        return candidate;
    }, [data?.timeZone]);
}
